import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Helmet } from "react-helmet";
import {
  Search,
  Filter,
  Users,
  Zap,
  Brain,
  Sparkles,
  Heart,
  ArrowUpDown,
  BarChart,
  Clock,
  Star,
  Award,
  Save,
  BookmarkCheck,
  Briefcase,
  ArrowDownUp,
  MessageSquare,
  Download,
  ChevronDown,
  X,
  History,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { FreelancerFilters } from "@/components/freelancers/FreelancerFilters";
import { FreelancerGrid } from "@/components/freelancers/FreelancerGrid";
import { freelancers } from "@/data/freelancers";
import { FreelancerProps } from "@/components/freelancers/FreelancerCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useProfileStore } from "@/components/profile/ProfileData";
import { useUserStore } from "@/store/userStore";
import { JobMatchScore } from "@/components/job-matching/JobMatchScore";
import { AnalyzingState } from "@/components/profile/ai-assistant/AnalyzingState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FreelancerComparison } from "@/components/freelancers/FreelancerComparison";
import { motion, AnimatePresence } from "framer-motion";
import { cn, debounce } from "@/lib/utils";
import {
  AllFreelancerProps,
  useFreelancerStore,
} from "@/store/freelancerStore";
import ReactPaginate from "react-paginate";
import { Pagination } from "@/components/ui/pagination-component";
import LoaderOverlay from "@/components/common/LoaderOverlay";
import { useNavigate } from "react-router-dom";
import Profile from "./Profile";

const Freelancers = () => {
  const { toast } = useToast();
  const { user } = useUserStore();
  const ref = useRef<boolean>(false);
  const {
    freelancerList: freelancers,
    fetchAllFreelancer,
    isLoading,
  } = useFreelancerStore();

  const [fetchDataProps, setFetchDataProps] = useState<AllFreelancerProps>({
    search: "",
    skills: "",
    rating: "",
    category: "",
    dailyRate: "",
    availability: true,
    sortBy: "createdAt",
    order: "desc",
    page: 1,
    limit: 9,
  });
  const { isPremium } = useUserStore();
  const { profileData } = useProfileStore();
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [rateRange, setRateRange] = useState("all");
  const [rating, setRating] = useState("all");
  const [filteredFreelancers, setFilteredFreelancers] = useState<
    FreelancerProps[]
  >([]);
  // const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("browse");
  const [aiFreelancers, setAiFreelancers] = useState<
    (FreelancerProps & { matchScore?: number })[]
  >([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [projectDescription, setProjectDescription] = useState("");

  // State variables
  const [sortBy, setSortBy] = useState("rating");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [savedFreelancers, setSavedFreelancers] = useState<string[]>([]);
  const [
    selectedFreelancersForComparison,
    setSelectedFreelancersForComparison,
  ] = useState<string[]>([]);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [experienceFilter, setExperienceFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [englishLevelFilter, setEnglishLevelFilter] = useState("all");
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const [exportFormat, setExportFormat] = useState<"pdf" | "csv" | "xlsx">(
    "pdf"
  );
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list" | "detailed">(
    "grid"
  );
  const [aiMatchingLevel, setAiMatchingLevel] = useState<
    "basic" | "advanced" | "expert"
  >(isPremium ? "advanced" : "basic");

  // Load saved freelancers from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("savedFreelancers");
    if (saved) {
      setSavedFreelancers(JSON.parse(saved));
    }

    const searches = localStorage.getItem("recentFreelancerSearches");
    if (searches) {
      setRecentSearches(JSON.parse(searches));
    }
  }, []);

  // Main filtering logic
  // useEffect(() => {
  //   let result = [...freelancers];

  //   if (searchTerm) {
  //     result = result.filter(
  //       (freelancer) =>
  //         freelancer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //         freelancer.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //         freelancer.description
  //           .toLowerCase()
  //           .includes(searchTerm.toLowerCase())
  //     );

  //     // Add to recent searches if not already there
  //     if (
  //       searchTerm.trim().length > 2 &&
  //       !recentSearches.includes(searchTerm.trim())
  //     ) {
  //       const updatedSearches = [
  //         searchTerm.trim(),
  //         ...recentSearches.slice(0, 4),
  //       ];
  //       setRecentSearches(updatedSearches);
  //       localStorage.setItem(
  //         "recentFreelancerSearches",
  //         JSON.stringify(updatedSearches)
  //       );
  //     }
  //   }

  //   if (selectedSkills.length > 0 && !selectedSkills.includes("all")) {
  //     result = result.filter((freelancer) =>
  //       selectedSkills.some((skill) => freelancer.skills.includes(skill))
  //     );
  //   }

  //   if (availabilityFilter !== "all") {
  //     result = result.filter(
  //       (freelancer) => freelancer.availability === availabilityFilter
  //     );
  //   }

  //   if (rateRange !== "all") {
  //     const [min, max] = rateRange.split("-").map(Number);
  //     result = result.filter((freelancer) => {
  //       if (rateRange === "100+") {
  //         return freelancer.dailyRate >= 100;
  //       }
  //       return freelancer.dailyRate >= min && freelancer.dailyRate <= max;
  //     });
  //   }

  //   // Additional filters
  //   if (experienceFilter !== "all") {
  //     const [min, max] = experienceFilter.split("-").map(Number);
  //     result = result.filter((freelancer) => {
  //       if (experienceFilter === "10+") {
  //         return freelancer.yearsOfExperience >= 10;
  //       }
  //       return (
  //         freelancer.yearsOfExperience >= min &&
  //         freelancer.yearsOfExperience <= max
  //       );
  //     });
  //   }

  //   if (locationFilter !== "all") {
  //     result = result.filter((freelancer) =>
  //       freelancer.location.includes(locationFilter)
  //     );
  //   }

  //   if (englishLevelFilter !== "all") {
  //     result = result.filter(
  //       (freelancer) => freelancer.englishLevel === englishLevelFilter
  //     );
  //   }

  //   setFilteredFreelancers(result);
  // }, [
  //   searchTerm,
  //   selectedSkills,
  //   availabilityFilter,
  //   rateRange,
  //   experienceFilter,
  //   locationFilter,
  //   englishLevelFilter,
  //   recentSearches,
  // ]);



  const handleAIMatching = useCallback(() => {
    if (!projectDescription.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter your project description for AI analysis",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);

    setTimeout(() => {
      const matchedFreelancers = freelancers.freelancers.map((freelancer) => {
        const userSkillsOverlap = profileData.skills.filter((skill) =>
          freelancer.skills.includes(skill)
        ).length;

        // Base match calculation
        const skillsMatchScore =
          (userSkillsOverlap / Math.max(profileData.skills.length, 1)) * 60;

        const keywordMatchScore = freelancer.skills.reduce((score, skill) => {
          return projectDescription.toLowerCase().includes(skill.toLowerCase())
            ? score + 6
            : score;
        }, 0);

        const ratingScore = (freelancer.rating / 5) * 15;

        // Enhanced matching for premium users and advanced AI level
        let complexityFactor = 1;
        let experienceFactor = 1;

        if (aiMatchingLevel === "advanced" || aiMatchingLevel === "expert") {
          // Add complexity analysis
          const complexityTerms = [
            "complex",
            "challenging",
            "difficult",
            "sophisticated",
          ];
          const hasComplexity = complexityTerms.some((term) =>
            projectDescription.toLowerCase().includes(term)
          );

          complexityFactor = hasComplexity
            ? freelancer.yearsOfExperience > 5
              ? 1.2
              : 0.8
            : 1;

          // Add experience weighting
          experienceFactor =
            freelancer.yearsOfExperience > 7
              ? 1.15
              : freelancer.yearsOfExperience > 3
              ? 1.05
              : 0.95;
        }

        // Expert level adds more nuanced analysis
        if (aiMatchingLevel === "expert") {
          // Simulate more sophisticated analysis
          const urgencyTerms = ["urgent", "asap", "quickly", "deadline"];
          const hasUrgency = urgencyTerms.some((term) =>
            projectDescription.toLowerCase().includes(term)
          );

          if (hasUrgency && freelancer.availability !== "Full-time") {
            complexityFactor *= 0.8;
          }
        }

        // Calculate final score with all factors
        let matchScore = Math.min(
          (skillsMatchScore + keywordMatchScore + ratingScore) *
            complexityFactor *
            experienceFactor +
            Math.random() * (isPremium ? 3 : 8),
          98
        );

        // Less precise for non-premium
        if (!isPremium) {
          matchScore = Math.floor(matchScore / 5) * 5;
        }

        return {
          ...freelancer,
          matchScore: Math.round(matchScore),
        };
      });

      const sortedFreelancers = [...matchedFreelancers].sort(
        (a, b) => (b.matchScore || 0) - (a.matchScore || 0)
      );

      setAiFreelancers(sortedFreelancers);
      setIsAnalyzing(false);
      setSortBy("matchScore");

      toast({
        title: "AI Analysis Complete",
        description: `Found ${sortedFreelancers.length} potential freelancers for your project`,
        variant: "success",
      });
    }, 2500);
  }, [
    toast,
    projectDescription,
    profileData.skills,
    isPremium,
    aiMatchingLevel,
  ]);

  // Toggle sort direction
  const toggleSortDirection = useCallback(() => {
    setFetchDataProps((prev) => ({
      ...prev,
      order: prev.order === "asc" ? "desc" : "asc",
      page: 1,
    }));
  }, []);

  // Handle freelancer save/bookmark
  const toggleSaveFreelancer = useCallback(
    (id: string) => {
      setSavedFreelancers((prev) => {
        const newSaved = prev.includes(id)
          ? prev.filter((fId) => fId !== id)
          : [...prev, id];

        localStorage.setItem("savedFreelancers", JSON.stringify(newSaved));
        return newSaved;
      });

      toast({
        title: savedFreelancers.includes(id)
          ? "Freelancer removed from saved"
          : "Freelancer saved",
        variant: savedFreelancers.includes(id) ? "default" : "success",
      });
    },
    [savedFreelancers, toast]
  );

  // Handle adding/removing freelancers for comparison
  const toggleFreelancerForComparison = useCallback(
    (id: string) => {
      setSelectedFreelancersForComparison((prev) => {
        if (prev.includes(id)) {
          return prev.filter((fId) => fId !== id);
        } else {
          if (prev.length >= 3) {
            toast({
              title: "Comparison limit reached",
              description: "You can compare up to 3 freelancers at once",
              variant: "destructive",
            });
            return prev;
          }
          return [...prev, id];
        }
      });
    },
    [toast]
  );

  // Handle export functionality
  const handleExport = useCallback(() => {
    setIsExporting(true);

    setTimeout(() => {
      setIsExporting(false);
      setIsExportDialogOpen(false);

      toast({
        title: "Export Successful",
        description: `Freelancer data exported as ${exportFormat.toUpperCase()}`,
        variant: "success",
      });
    }, 1500);
  }, [exportFormat, toast]);

  // Get freelancers for comparison based on selected IDs
  // const freelancersForComparison = useMemo(() => {
  //   const allFreelancers = activeTab === "ai" ? aiFreelancers : freelancers;
  //   return allFreelancers.filter(f => selectedFreelancersForComparison.includes(f.id));
  // }, [selectedFreelancersForComparison, aiFreelancers, activeTab, freelancers]);

  const getAllFreelancers = async (data: any) => {
    const payload = {
      search: "",
      skills: "",
      rating: 0,
      category: "",
      dailyRate: "",
      availability: true,
      sortBy: "createdAt",
      order: "desc",
      page: 1,
      limit: 9,
    };
    const updatedData = data ? data : payload;
    try {
      await fetchAllFreelancer(updatedData, user?.accessToken);
    } catch (err) {
      console.log("Error:", err);
    }
  };

  const clearSearch = useCallback(() => {
    setSearchTerm("");
    debouncedFetchJobs("");
  }, []);

  const handlePageChange = (selectedPage: number) => {
    if(ref.current){
      setFetchDataProps({ ...fetchDataProps, page: selectedPage });
      setPage(selectedPage);
      getAllFreelancers({ ...fetchDataProps, page: selectedPage });
    }
  };

  const handleSearchTerms = (searchTerm: string) => {
    setFetchDataProps({
      ...fetchDataProps,
      search: searchTerm,
    });
    const payload = {
      search: searchTerm,
      skills: "",
      rating: 0,
      category: "",
      dailyRate: "",
      availability: true,
      sortBy: "createdAt",
      order: "desc",
      page: 1,
      limit: 9,
    };
    getAllFreelancers(payload);
  };

  const debouncedFetchJobs = useCallback(debounce(handleSearchTerms, 500), []);

  useEffect(() => {
    getAllFreelancers(fetchDataProps);
    ref.current = true;
  }, []);
  
  return (
    <>
      {isLoading && <LoaderOverlay />}
      <div className="container py-8 animate-in fade-in duration-500">
        <Helmet>
          <title>Find Freelancers | DGK Freelance Hub</title>
        </Helmet>

        <div className="flex flex-col gap-8">
          <section className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  Find Freelancers
                </h1>
                <p className="text-gray-600 mt-1">
                  Connect with top talent for your projects
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="gap-1.5 border-gray-200 shadow-sm hover:shadow-md transition-all"
                    >
                      <ArrowUpDown className="h-4 w-4" />
                      Sort by
                      <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent className="w-56 animate-in slide-in-from-top-5 fade-in-50 duration-200">
                    <DropdownMenuLabel>Sort Direction</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    <DropdownMenuItem
                      onClick={() => {
                        setSortDirection("asc");
                        setFetchDataProps((prev) => ({
                          ...prev,
                          order: "asc",
                        }));
                      }}
                      className="flex justify-between"
                    >
                      Ascending
                      {sortDirection === "asc" && (
                        <ArrowUpDown className="h-4 w-4 text-blue-500" />
                      )}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={() => {
                        setSortDirection("desc");
                        setFetchDataProps((prev) => ({
                          ...prev,
                          order: "desc",
                        }));
                      }}
                      className="flex justify-between"
                    >
                      Descending
                      {sortDirection === "desc" && (
                        <ArrowDownUp className="h-4 w-4 text-blue-500" />
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* <Button
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={cn(
                    "transition-all duration-300 gap-2 font-medium",
                    showAdvancedFilters
                      ? "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg shadow-purple-200"
                      : "bg-gradient-to-r from-emerald-600 to-sky-600 hover:from-emerald-700 hover:to-sky-700 shadow-lg shadow-emerald-200"
                  )}
                >
                  <Filter className="h-4 w-4" />
                  {showAdvancedFilters ? "Hide Filters" : "Advanced Filters"}
                </Button> */}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="gap-1.5 border-gray-200 shadow-sm hover:shadow-md transition-all"
                    >
                      <Users className="h-4 w-4" />
                      View
                      <ChevronDown className="h-4 w-4 ml-1 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 animate-in slide-in-from-top-5 fade-in-50 duration-200">
                    {/* <DropdownMenuLabel>Browse Options</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem>
                      <Briefcase className="h-4 w-4 mr-2 text-gray-500" />
                      Browse Agencies
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Award className="h-4 w-4 mr-2 text-amber-500" />
                      Top Rated Freelancers
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Sparkles className="h-4 w-4 mr-2 text-purple-500" />
                      Rising Talent
                    </DropdownMenuItem> */}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Layout Options</DropdownMenuLabel>
                    <DropdownMenuCheckboxItem
                      checked={viewMode === "grid"}
                      onCheckedChange={() => setViewMode("grid")}
                    >
                      Grid View
                    </DropdownMenuCheckboxItem>
                    <DropdownMenuCheckboxItem
                      checked={viewMode === "list"}
                      onCheckedChange={() => setViewMode("list")}
                    >
                      List View
                    </DropdownMenuCheckboxItem>
                    {/* <DropdownMenuCheckboxItem
                      checked={viewMode === "detailed"}
                      onCheckedChange={() => setViewMode("detailed")}
                    >
                      Detailed View
                    </DropdownMenuCheckboxItem> */}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <AnimatePresence>
              {selectedFreelancersForComparison.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-blue-50 p-3 rounded-lg border border-blue-100 flex justify-between items-center shadow-sm"
                >
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="secondary"
                      className="bg-blue-100 text-blue-700 border-blue-200 font-medium"
                    >
                      {selectedFreelancersForComparison.length} selected
                    </Badge>
                    <span className="text-sm text-blue-700">
                      {selectedFreelancersForComparison.length === 1
                        ? "1 freelancer selected for comparison"
                        : `${selectedFreelancersForComparison.length} freelancers selected for comparison`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-8 border-blue-200 hover:bg-blue-100 text-blue-700"
                      onClick={() => setSelectedFreelancersForComparison([])}
                    >
                      Clear
                    </Button>
                    <Button
                      size="sm"
                      className="text-xs h-8 bg-blue-600 hover:bg-blue-700 shadow-sm"
                      onClick={() => setIsComparisonModalOpen(true)}
                      disabled={selectedFreelancersForComparison.length < 2}
                    >
                      <BarChart className="h-3.5 w-3.5 mr-1.5" />
                      Compare Freelancers
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="mt-6"
            >
              <TabsList className="grid w-full grid-cols-2 mb-6 rounded-xl shadow-sm">
                <TabsTrigger
                  value="browse"
                  className={`py-3 ${
                    activeTab === "browse" ? "shadow-sm" : ""
                  }`}
                >
                  <Search className="h-4 w-4 mr-2" />
                  Browse Freelancers
                </TabsTrigger>
                <TabsTrigger
                  value="ai"
                  className={`py-3 ${activeTab === "ai" ? "shadow-sm" : ""}`}
                >
                  <Zap className="h-4 w-4 mr-2 text-amber-500" />
                  AI Matching
                </TabsTrigger>
              </TabsList>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {activeTab === "browse" && (
                    <Card className="border-none shadow-md overflow-hidden">
                      <CardHeader className="pb-2 bg-gradient-to-b from-gray-50 to-transparent">
                        <CardTitle className="text-lg">
                          Find the perfect match for your project
                        </CardTitle>
                        <CardDescription>
                          Use filters to narrow down your search
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-6">
                          <div className="relative">
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                  type="text"
                                  placeholder="Search for skills, job titles, or keywords..."
                                  value={searchTerm}
                                  onChange={(e) => {
                                    const searchText = e.target.value;
                                    setSearchTerm(searchText);
                                    debouncedFetchJobs(searchText);
                                  }}
                                  className="pl-10 pr-10 border-gray-200 focus:border-blue-300 focus:ring-blue-200 transition-all shadow-sm"
                                  onFocus={() => setShowRecentSearches(true)}
                                  onBlur={() =>
                                    setTimeout(
                                      () => setShowRecentSearches(false),
                                      200
                                    )
                                  }
                                />
                                <AnimatePresence>
                                  {searchTerm && (
                                    <motion.div
                                      initial={{ opacity: 0, scale: 0.9 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      exit={{ opacity: 0, scale: 0.9 }}
                                      transition={{ duration: 0.15 }}
                                    >
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400 hover:text-gray-700"
                                        onClick={clearSearch}
                                      >
                                        <X className="h-4 w-4" />
                                      </Button>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                              {/* <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={() =>
                                        setIsExportDialogOpen(true)
                                      }
                                      className="border-gray-200 shadow-sm hover:shadow-md transition-all"
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Export Results</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider> */}
                            </div>

                            <AnimatePresence>
                              {showRecentSearches &&
                                recentSearches.length > 0 && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 5 }}
                                    className="absolute z-10 mt-1 w-full bg-white rounded-md border shadow-lg py-2"
                                  >
                                    <div className="px-3 py-1 text-xs font-medium text-gray-500 flex items-center gap-1.5">
                                      <Clock className="h-3 w-3" />
                                      Recent Searches
                                    </div>
                                    {recentSearches.map((search, index) => (
                                      <div
                                        key={index}
                                        className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer flex items-center gap-2 transition-colors group"
                                        onClick={() => setSearchTerm(search)}
                                      >
                                        <History className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600" />
                                        <span className="text-gray-700">
                                          {search}
                                        </span>
                                      </div>
                                    ))}
                                  </motion.div>
                                )}
                            </AnimatePresence>
                          </div>

                          <FreelancerFilters
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            selectedSkills={selectedSkills}
                            setSelectedSkills={setSelectedSkills}
                            availabilityFilter={availabilityFilter}
                            setAvailabilityFilter={setAvailabilityFilter}
                            rateRange={rateRange}
                            setRateRange={setRateRange}
                            fetchDataProps={fetchDataProps}
                            debouncedFetchJobs={debouncedFetchJobs}
                            setFetchDataProps={setFetchDataProps}
                            getAllFreelancers={getAllFreelancers}
                            rating={rating}
                            setRating ={setRating}
                          />

                          <AnimatePresence>
                            {showAdvancedFilters && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                              >
                                <div className="pt-4 border-t space-y-4">
                                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                                    <Filter className="h-4 w-4 text-indigo-500" />
                                    Advanced Filters
                                  </h3>

                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                      <Label
                                        htmlFor="experienceFilter"
                                        className="flex items-center gap-1.5"
                                      >
                                        <Briefcase className="h-3.5 w-3.5 text-gray-500" />
                                        Years of Experience
                                      </Label>
                                      <select
                                        id="experienceFilter"
                                        value={experienceFilter}
                                        onChange={(e) =>
                                          setExperienceFilter(e.target.value)
                                        }
                                        className="w-full rounded-md border border-gray-200 p-2 text-sm shadow-sm focus:border-indigo-300 focus:ring-indigo-200 transition-all"
                                      >
                                        <option value="all">
                                          All Experience Levels
                                        </option>
                                        <option value="0-1">
                                          Less than 1 year
                                        </option>
                                        <option value="1-3">1-3 years</option>
                                        <option value="3-5">3-5 years</option>
                                        <option value="5-10">5-10 years</option>
                                        <option value="10+">10+ years</option>
                                      </select>
                                    </div>

                                    <div className="space-y-2">
                                      <Label
                                        htmlFor="locationFilter"
                                        className="flex items-center gap-1.5"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="h-3.5 w-3.5 text-gray-500"
                                        >
                                          <path d="M12 22s-8-4.5-8-11.8A8 8 0 0 1 12 2a8 8 0 0 1 8 8.2c0 7.3-8 11.8-8 11.8z" />
                                          <circle cx="12" cy="10" r="3" />
                                        </svg>
                                        Location
                                      </Label>
                                      <select
                                        id="locationFilter"
                                        value={locationFilter}
                                        onChange={(e) =>
                                          setLocationFilter(e.target.value)
                                        }
                                        className="w-full rounded-md border border-gray-200 p-2 text-sm shadow-sm focus:border-indigo-300 focus:ring-indigo-200 transition-all"
                                      >
                                        <option value="all">
                                          All Locations
                                        </option>
                                        <option value="USA">
                                          United States
                                        </option>
                                        <option value="Europe">Europe</option>
                                        <option value="Asia">Asia</option>
                                        <option value="Remote">
                                          Remote Only
                                        </option>
                                      </select>
                                    </div>

                                    <div className="space-y-2">
                                      <Label
                                        htmlFor="englishLevelFilter"
                                        className="flex items-center gap-1.5"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          className="h-3.5 w-3.5 text-gray-500"
                                        >
                                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        </svg>
                                        English Level
                                      </Label>
                                      <select
                                        id="englishLevelFilter"
                                        value={englishLevelFilter}
                                        onChange={(e) =>
                                          setEnglishLevelFilter(e.target.value)
                                        }
                                        className="w-full rounded-md border border-gray-200 p-2 text-sm shadow-sm focus:border-indigo-300 focus:ring-indigo-200 transition-all"
                                      >
                                        <option value="all">Any Level</option>
                                        <option value="Native">Native</option>
                                        <option value="Fluent">Fluent</option>
                                        <option value="Conversational">
                                          Conversational
                                        </option>
                                        <option value="Basic">Basic</option>
                                      </select>
                                    </div>
                                  </div>

                                  <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
                                    <Label className="text-sm text-gray-700 font-medium">
                                      Additional Options
                                    </Label>
                                    <div className="flex flex-wrap gap-4 mt-2">
                                      <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded border border-gray-100 shadow-sm">
                                        <Checkbox
                                          id="verifiedOnly"
                                          className="text-indigo-500 focus:ring-indigo-200"
                                        />
                                        <Label
                                          htmlFor="verifiedOnly"
                                          className="text-sm font-normal cursor-pointer"
                                        >
                                          Verified only
                                        </Label>
                                      </div>
                                      <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded border border-gray-100 shadow-sm">
                                        <Checkbox
                                          id="withPortfolio"
                                          className="text-indigo-500 focus:ring-indigo-200"
                                        />
                                        <Label
                                          htmlFor="withPortfolio"
                                          className="text-sm font-normal cursor-pointer"
                                        >
                                          With portfolio
                                        </Label>
                                      </div>
                                      <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded border border-gray-100 shadow-sm">
                                        <Checkbox
                                          id="withTestScores"
                                          className="text-indigo-500 focus:ring-indigo-200"
                                        />
                                        <Label
                                          htmlFor="withTestScores"
                                          className="text-sm font-normal cursor-pointer"
                                        >
                                          With test scores
                                        </Label>
                                      </div>
                                      <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded border border-gray-100 shadow-sm">
                                        <Checkbox
                                          id="previouslyHired"
                                          className="text-indigo-500 focus:ring-indigo-200"
                                        />
                                        <Label
                                          htmlFor="previouslyHired"
                                          className="text-sm font-normal cursor-pointer"
                                        >
                                          Previously hired
                                        </Label>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {activeTab === "ai" && (
                    <Card className="border-none shadow-md overflow-hidden">
                      <CardHeader className="pb-2 bg-gradient-to-r from-emerald-50 to-sky-50">
                        <CardTitle className="flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-sky-600">
                          <Brain className="h-5 w-5 text-emerald-600" />
                          AI-Powered Freelancer Matching
                        </CardTitle>
                        <CardDescription>
                          Our AI analyzes your project needs and finds the best
                          freelancers based on skills, experience, and work
                          style
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <div className="space-y-4">
                          <div>
                            <label
                              htmlFor="projectDescription"
                              className="block text-sm font-medium mb-1.5 flex items-center gap-1.5"
                            >
                              <MessageSquare className="h-4 w-4 text-gray-500" />
                              Describe your project or requirements
                            </label>
                            <Textarea
                              id="projectDescription"
                              value={projectDescription}
                              onChange={(e) =>
                                setProjectDescription(e.target.value)
                              }
                              placeholder="Describe your project, required skills, timeline, and any specific requirements..."
                              className="resize-none min-h-[120px] border-gray-200 shadow-sm focus:border-emerald-300 focus:ring-emerald-200 transition-all"
                            />

                            {/* Character count and suggestions */}
                            <div className="flex justify-between mt-1.5 text-xs text-gray-500">
                              <div
                                className={
                                  projectDescription.length > 400
                                    ? "text-amber-600"
                                    : ""
                                }
                              >
                                {projectDescription.length} / 500 characters
                              </div>
                              {projectDescription.length > 0 &&
                                projectDescription.length < 50 && (
                                  <div className="flex items-center gap-1 text-amber-600">
                                    <AlertCircle className="h-3 w-3" />
                                    Add more details for better matches
                                  </div>
                                )}
                            </div>

                            {/* AI Matching Level selector (visible for premium users) */}
                            {isPremium && (
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-5 space-y-2 bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-lg border border-gray-200"
                              >
                                <Label
                                  htmlFor="aiMatchingLevel"
                                  className="text-sm font-medium flex items-center gap-1.5"
                                >
                                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                                  AI Matching Precision
                                </Label>
                                <div className="grid grid-cols-3 gap-2">
                                  <Button
                                    type="button"
                                    variant={
                                      aiMatchingLevel === "basic"
                                        ? "default"
                                        : "outline"
                                    }
                                    className={cn(
                                      "h-auto py-2 text-xs rounded-md transition-all",
                                      aiMatchingLevel === "basic"
                                        ? "bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 shadow-md shadow-sky-200/50"
                                        : "border-gray-200 shadow-sm hover:shadow"
                                    )}
                                    onClick={() => setAiMatchingLevel("basic")}
                                  >
                                    Basic
                                  </Button>
                                  <Button
                                    type="button"
                                    variant={
                                      aiMatchingLevel === "advanced"
                                        ? "default"
                                        : "outline"
                                    }
                                    className={cn(
                                      "h-auto py-2 text-xs rounded-md transition-all",
                                      aiMatchingLevel === "advanced"
                                        ? "bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-md shadow-emerald-200/50"
                                        : "border-gray-200 shadow-sm hover:shadow"
                                    )}
                                    onClick={() =>
                                      setAiMatchingLevel("advanced")
                                    }
                                  >
                                    Advanced
                                  </Button>
                                  <Button
                                    type="button"
                                    variant={
                                      aiMatchingLevel === "expert"
                                        ? "default"
                                        : "outline"
                                    }
                                    className={cn(
                                      "h-auto py-2 text-xs rounded-md transition-all",
                                      aiMatchingLevel === "expert"
                                        ? "bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 shadow-md shadow-purple-200/50"
                                        : "border-gray-200 shadow-sm hover:shadow"
                                    )}
                                    onClick={() => setAiMatchingLevel("expert")}
                                  >
                                    Expert
                                  </Button>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  {aiMatchingLevel === "basic"
                                    ? "Basic matching uses skills and keywords to find suitable freelancers"
                                    : aiMatchingLevel === "advanced"
                                    ? "Advanced matching includes project complexity and experience factors"
                                    : "Expert matching adds nuanced analysis of availability, communication style and more"}
                                </p>
                              </motion.div>
                            )}
                          </div>

                          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-2">
                            <p className="text-sm text-gray-500">
                              {isPremium ? (
                                <span className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 border border-amber-100 rounded-full shadow-sm">
                                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                                  Premium matching enabled
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-gray-600">
                                  <Badge
                                    variant="outline"
                                    className="font-normal border-amber-200 text-amber-700 bg-amber-50 mr-1"
                                  >
                                    PRO
                                  </Badge>
                                  Upgrade for enhanced matching
                                  <InfoTooltip content="Premium members get more precise matching scores and additional filtering options" />
                                </span>
                              )}
                            </p>
                            <Button
                              onClick={handleAIMatching}
                              disabled={isAnalyzing}
                              className={cn(
                                "gap-2 transition-all shadow-lg text-white font-medium",
                                isAnalyzing
                                  ? "bg-amber-500"
                                  : "bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-emerald-600 hover:to-sky-600 shadow-emerald-200/50"
                              )}
                            >
                              {isAnalyzing ? (
                                <>
                                  <span className="flex items-center gap-1">
                                    <svg
                                      className="animate-spin h-4 w-4 text-white"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    Analyzing
                                  </span>
                                </>
                              ) : (
                                <>
                                  <Zap className="w-4 h-4" /> Find Matches
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </motion.div>
              </AnimatePresence>
            </Tabs>
          </section>

          <section className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="text-gray-600">
                {activeTab === "ai" && aiFreelancers.length > 0 ? (
                  <span className="font-medium flex items-center gap-1.5">
                    <Brain className="h-4 w-4 text-emerald-500" />
                    AI-matched freelancers for your project
                  </span>
                ) : (
                  <span>
                    Showing{" "}
                    <span className="font-medium text-gray-900">
                      {freelancers?.total || 0}
                    </span>{" "}
                    freelancers
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600 flex items-center gap-1.5">
                Sort by:
                <span className="font-medium text-emerald-600">
                  {sortBy === "rating"
                    ? "Highest Rated"
                    : sortBy === "dailyRate"
                    ? "Daily Rate"
                    : sortBy === "experience"
                    ? "Experience"
                    : sortBy === "saved"
                    ? "Saved Freelancers"
                    : "Match Score"}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 hover:bg-gray-100 transition-colors"
                  onClick={toggleSortDirection}
                >
                  <ArrowDownUp className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {activeTab === "ai" && isAnalyzing ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="h-96 flex flex-col items-center justify-center"
                >
                  <AnalyzingState
                    message="Analyzing your project requirements..."
                    altMessages={[
                      "Matching with qualified freelancers...",
                      "Evaluating skills and experience...",
                      "Calculating compatibility scores...",
                      "Finding the perfect match for your project...",
                    ]}
                    icon={
                      <Brain className="h-14 w-14 text-emerald-500 opacity-80" />
                    }
                  />
                </motion.div>
              ) : activeTab === "ai" && aiFreelancers.length > 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <FreelancerGridWithActions
                    // freelancers={sortedFreelancers}
                    freelancers={freelancers?.freelancers}
                    savedFreelancers={savedFreelancers}
                    selectedForComparison={selectedFreelancersForComparison}
                    toggleSave={toggleSaveFreelancer}
                    toggleCompare={toggleFreelancerForComparison}
                    viewMode={viewMode}
                    showMatchScore={true}
                    isPremium={isPremium}
                  />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <FreelancerGridWithActions
                    // freelancers={sortedFreelancers}
                    freelancers={freelancers?.freelancers}
                    savedFreelancers={savedFreelancers}
                    selectedForComparison={selectedFreelancersForComparison}
                    toggleSave={toggleSaveFreelancer}
                    toggleCompare={toggleFreelancerForComparison}
                    viewMode={viewMode}
                    isLoading={isLoading}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {!isLoading &&
                freelancers?.freelancers?.length === 0 &&
                activeTab === "browse" && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-16 bg-gray-50 rounded-xl border border-gray-100"
                  >
                    <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-medium text-gray-700 mb-2">
                      No freelancers found
                    </h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                      Try adjusting your search criteria or filters to find more
                      freelancers
                    </p>
                    <Button
                      variant="outline"
                      className="mt-4 border-gray-200"
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedSkills([]);
                        setAvailabilityFilter("all");
                        setRateRange("all");
                        setExperienceFilter("all");
                        setLocationFilter("all");
                        setEnglishLevelFilter("all");
                        getAllFreelancers(null);
                        setFetchDataProps({
                          search: "",
                          skills: "",
                          rating: 0,
                          category: "",
                          dailyRate: "",
                          availability: true,
                          sortBy: "createdAt",
                          order: "desc",
                          page: 1,
                          limit: 9,
                        });
                      }}
                    >
                      Reset Filters
                    </Button>
                  </motion.div>
                )}

              {Number(freelancers?.total ?? 0) >
                Number(fetchDataProps?.limit ?? 0) && (
                <Pagination
                  totalPages={freelancers.totalPages}
                  onPageChange={handlePageChange}
                  // initialPage={fetchDataProps?.page}
                  forcePage={(fetchDataProps.page || 1) - 1}
                  className="mt-6"
                />
              )}

              {activeTab === "ai" &&
                !isAnalyzing &&
                aiFreelancers.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center py-16 bg-gradient-to-r from-emerald-50 to-sky-50 rounded-xl border border-gray-100"
                  >
                    <Brain className="h-16 w-16 text-emerald-500 opacity-40 mx-auto mb-4" />
                    <h3 className="text-xl font-medium bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-transparent mb-2">
                      AI Matching Ready
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto">
                      Enter your project description above and our AI will match
                      you with the perfect freelancers
                    </p>
                    {isPremium && (
                      <Badge className="mt-4 bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200">
                        <Sparkles className="h-3 w-3 mr-1" />
                        Premium Matching Available
                      </Badge>
                    )}
                  </motion.div>
                )}
            </AnimatePresence>
          </section>
        </div>

        {/* Comparison Modal */}
        {/* <Dialog open={isComparisonModalOpen} onOpenChange={setIsComparisonModalOpen}>
        <DialogContent className="max-w-4xl w-full animate-in slide-in-from-bottom-10 fade-in-50 duration-300">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <BarChart className="h-5 w-5 text-blue-500" />
              Compare Freelancers
            </DialogTitle>
            <DialogDescription>
              Side-by-side comparison of selected freelancers
            </DialogDescription>
          </DialogHeader>
          
          <FreelancerComparison 
            freelancers={freelancersForComparison}
            onClose={() => setIsComparisonModalOpen(false)}
          />
          
          <DialogFooter className="gap-2 sm:gap-0 mt-6">
            <Button 
              variant="outline" 
              onClick={() => setIsComparisonModalOpen(false)}
              className="border-gray-200"
            >
              Close
            </Button>
            <Button 
              onClick={() => {
                toast({
                  title: "Contact initiated",
                  description: "Message template created for selected freelancers",
                  variant: "success",
                });
                setIsComparisonModalOpen(false);
              }}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Contact Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}

        {/* Export Dialog */}
        <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
          <DialogContent className="animate-in slide-in-from-bottom-10 fade-in-50 duration-300">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download className="h-5 w-5 text-gray-500" />
                Export Freelancer Data
              </DialogTitle>
              <DialogDescription>
                Select a format to export the current freelancer results
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="font-medium">Export Format</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={exportFormat === "pdf" ? "default" : "outline"}
                    className={cn(
                      "rounded-md transition-all",
                      exportFormat === "pdf"
                        ? "bg-blue-600 hover:bg-blue-700 shadow-md shadow-blue-100"
                        : "border-gray-200 hover:border-gray-300 shadow-sm"
                    )}
                    onClick={() => setExportFormat("pdf")}
                  >
                    PDF
                  </Button>
                  <Button
                    type="button"
                    variant={exportFormat === "csv" ? "default" : "outline"}
                    className={cn(
                      "rounded-md transition-all",
                      exportFormat === "csv"
                        ? "bg-green-600 hover:bg-green-700 shadow-md shadow-green-100"
                        : "border-gray-200 hover:border-gray-300 shadow-sm"
                    )}
                    onClick={() => setExportFormat("csv")}
                  >
                    CSV
                  </Button>
                  <Button
                    type="button"
                    variant={exportFormat === "xlsx" ? "default" : "outline"}
                    className={cn(
                      "rounded-md transition-all",
                      exportFormat === "xlsx"
                        ? "bg-emerald-600 hover:bg-emerald-700 shadow-md shadow-emerald-100"
                        : "border-gray-200 hover:border-gray-300 shadow-sm"
                    )}
                    onClick={() => setExportFormat("xlsx")}
                  >
                    Excel
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-medium">Export Options</Label>
                <div className="space-y-2 bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-100 shadow-sm">
                    <Checkbox
                      id="includeContact"
                      defaultChecked
                      className="text-blue-500 focus:ring-blue-200"
                    />
                    <Label
                      htmlFor="includeContact"
                      className="text-sm font-normal"
                    >
                      Include contact information
                    </Label>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-100 shadow-sm">
                    <Checkbox
                      id="includeRatings"
                      defaultChecked
                      className="text-blue-500 focus:ring-blue-200"
                    />
                    <Label
                      htmlFor="includeRatings"
                      className="text-sm font-normal"
                    >
                      Include ratings and reviews
                    </Label>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-white rounded border border-gray-100 shadow-sm">
                    <Checkbox
                      id="includePortfolio"
                      className="text-blue-500 focus:ring-blue-200"
                    />
                    <Label
                      htmlFor="includePortfolio"
                      className="text-sm font-normal"
                    >
                      Include portfolio links
                    </Label>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setIsExportDialogOpen(false)}
                className="border-gray-200"
              >
                Cancel
              </Button>
              <Button
                onClick={handleExport}
                disabled={isExporting}
                className={cn(
                  "gap-2 transition-all",
                  exportFormat === "pdf"
                    ? "bg-blue-600 hover:bg-blue-700"
                    : exportFormat === "csv"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-emerald-600 hover:bg-emerald-700"
                )}
              >
                {isExporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Export as {exportFormat.toUpperCase()}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

// Helper Component for the Tooltip with info icon
const InfoTooltip = ({ content }: { content: string }) => (
  <TooltipProvider>
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <AlertCircle className="h-3.5 w-3.5 text-gray-400 cursor-help ml-1" />
      </TooltipTrigger>
      <TooltipContent className="bg-gray-900 text-white">
        <p className="max-w-xs text-xs">{content}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Helper Component for Checkmark
const Check = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Enhanced Freelancer Grid with Actions (save and compare)
const FreelancerGridWithActions = ({
  freelancers,
  savedFreelancers,
  selectedForComparison,
  toggleSave,
  toggleCompare,
  viewMode,
  showMatchScore = false,
  isPremium = false,
  isLoading = false,
}) => {

  const navigate = useNavigate();

  const [isFreelancerProfileOpen, setIsFreelancerProfileOpen] = useState(false);
  const [freelancerProfileData, setFreelancerProfileData] = useState(null);

  const navigateToFreelancePageDetails = (freelancer: FreelancerProps) => {
    console.log(freelancer);

    setIsFreelancerProfileOpen(true);
    setFreelancerProfileData(freelancer);
  };

  const backToAIRecommFreelancer = () => {
    setIsFreelancerProfileOpen(false);
    setFreelancerProfileData(null);
    navigate(`/freelancers`);
  };

  if (isLoading) {
    return (
      <div
        className={`grid grid-cols-1 ${
          viewMode === "list"
            ? "md:grid-cols-1"
            : "md:grid-cols-2 lg:grid-cols-3"
        } gap-6`}
      >
        {[1, 2, 3, 4, 5, 6].map((_, index) => (
          <Card
            key={index}
            className="relative overflow-hidden h-[220px] animate-pulse"
          >
            <div className="h-full bg-gray-100"></div>
          </Card>
        ))}
      </div>
    );
  }

  // Add wrapper with action buttons to each freelancer card
  return (
    isFreelancerProfileOpen ? <Profile
      backToAIRecommFreelancer={backToAIRecommFreelancer}
      isFromContracPage={false}
      newFreelancerData={freelancerProfileData}
    /> : <div
      className={`grid grid-cols-1 ${viewMode === "list" ? "md:grid-cols-1" : "md:grid-cols-2 lg:grid-cols-3"
        } gap-6`}
    >
      {freelancers &&
        freelancers.length > 0 &&
        Array.isArray(freelancers) &&
        freelancers.map((freelancer) => (
          <motion.div
            key={freelancer._id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <Card className="relative overflow-hidden group border-gray-200 hover:border-gray-300 transition-all hover:shadow-md">
              {/* <div className="absolute top-[18px] right-0 z-10 flex gap-1 p-2">
                {/* <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className={cn(
                          "h-8 w-8 rounded-full bg-white shadow-sm transition-all",
                          savedFreelancers.includes(freelancer.id)
                            ? "text-pink-500 border-pink-200 shadow-pink-100"
                            : "text-gray-400 border-gray-200 opacity-80 group-hover:opacity-100"
                        )}
                        onClick={() => toggleSave(freelancer.id)}
                      >
                        <Heart
                          className={cn(
                            "h-4 w-4 transition-all",
                            savedFreelancers.includes(freelancer.id)
                              ? "fill-pink-500"
                              : "group-hover:text-gray-700"
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {savedFreelancers.includes(freelancer.id)
                        ? "Remove from saved"
                        : "Save freelancer"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider> 

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className={cn(
                          "h-8 w-8 rounded-full bg-white shadow-sm transition-all",
                          selectedForComparison.includes(freelancer.id)
                            ? "text-blue-600 border-blue-200 shadow-blue-100"
                            : "text-gray-400 border-gray-200 opacity-80 group-hover:opacity-100"
                        )}
                        onClick={() => toggleCompare(freelancer.id)}
                      >
                        <BarChart
                          className={cn(
                            "h-4 w-4 transition-all",
                            selectedForComparison.includes(freelancer.id)
                              ? ""
                              : "group-hover:text-gray-700"
                          )}
                        />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      {selectedForComparison.includes(freelancer.id)
                        ? "Remove from comparison"
                        : "Add to comparison"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div> */}

              {showMatchScore && freelancer.matchScore && (
                <div className="absolute top-3 left-3 z-10">
                  <JobMatchScore
                    score={freelancer.matchScore}
                    isPremium={isPremium}
                    type="freelancer"
                  />
                </div>
              )}

               <FreelancerGrid freelancers={[freelancer]} navigateToFreelancePageDetails={navigateToFreelancePageDetails}/>
            </Card>
          </motion.div>
        ))}
    </div>
  );
};

export default Freelancers;
